"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumNetLabelNamingRule = void 0;
const AltiumNetNamingRule_1 = require("./AltiumNetNamingRule");
const AltiumEnums_1 = require("../../common/AltiumEnums");
const AltiumSheetEntryNamingEligibility_1 = require("./AltiumSheetEntryNamingEligibility");
const AltiumNetNameCaseSelection_1 = require("./AltiumNetNameCaseSelection");
function compareLabelNamesOrdinal(a, b) {
    const foldedA = a.toLowerCase();
    const foldedB = b.toLowerCase();
    let aIndex = 0;
    let bIndex = 0;
    while (aIndex < foldedA.length && bIndex < foldedB.length) {
        const aCharacter = foldedA[aIndex];
        const bCharacter = foldedB[bIndex];
        const aIsDigit = aCharacter >= '0' && aCharacter <= '9';
        const bIsDigit = bCharacter >= '0' && bCharacter <= '9';
        if (aIsDigit && bIsDigit) {
            let aRunEnd = aIndex + 1;
            let bRunEnd = bIndex + 1;
            while (aRunEnd < foldedA.length && foldedA[aRunEnd] >= '0' && foldedA[aRunEnd] <= '9') {
                aRunEnd++;
            }
            while (bRunEnd < foldedB.length && foldedB[bRunEnd] >= '0' && foldedB[bRunEnd] <= '9') {
                bRunEnd++;
            }
            let aSignificantStart = aIndex;
            let bSignificantStart = bIndex;
            while (aSignificantStart < aRunEnd - 1 && foldedA[aSignificantStart] === '0') {
                aSignificantStart++;
            }
            while (bSignificantStart < bRunEnd - 1 && foldedB[bSignificantStart] === '0') {
                bSignificantStart++;
            }
            const aSignificantLength = aRunEnd - aSignificantStart;
            const bSignificantLength = bRunEnd - bSignificantStart;
            if (aSignificantLength !== bSignificantLength) {
                return aSignificantLength - bSignificantLength;
            }
            for (let digitIndex = 0; digitIndex < aSignificantLength; digitIndex++) {
                const aDigit = foldedA[aSignificantStart + digitIndex];
                const bDigit = foldedB[bSignificantStart + digitIndex];
                if (aDigit !== bDigit) {
                    return aDigit < bDigit ? -1 : 1;
                }
            }
            const aRunLength = aRunEnd - aIndex;
            const bRunLength = bRunEnd - bIndex;
            if (aRunLength !== bRunLength) {
                return aRunLength - bRunLength;
            }
            aIndex = aRunEnd;
            bIndex = bRunEnd;
            continue;
        }
        if (aCharacter !== bCharacter) {
            return aCharacter < bCharacter ? -1 : 1;
        }
        aIndex++;
        bIndex++;
    }
    if (aIndex !== foldedA.length || bIndex !== foldedB.length) {
        return aIndex === foldedA.length ? -1 : 1;
    }
    return (a === b ? 0
        : a < b ? -1
            : 1);
}
class AltiumNetLabelNamingRule extends AltiumNetNamingRule_1.AltiumNetNamingRule {
    apply(group, _hierarchy) {
        const canUseSheetEntries = this.config.allowSheetEntryNetNames && !this.config.allowPortNetNames;
        if (!group.hasLabelType('net-label') && (!canUseSheetEntries || !group.hasLabelType('sheet-entry'))) {
            return undefined;
        }
        const candidates = [];
        const netLabelByFold = new Map();
        const sheetEntryByFold = new Map();
        const busMemberFolds = new Set();
        for (const [instance, nets] of group.getNets()) {
            for (const net of nets) {
                for (const label of net.labels) {
                    if (label.labelType === 'net-label' && label.strategyMetadata?.busRangeNetLabelMember) {
                        busMemberFolds.add(label.originalLabel.toLowerCase());
                    }
                    if (label.labelType === 'net-label') {
                        if (!label.formattedLabel) {
                            continue;
                        }
                        const name = this.dereferenceLabelName(this.namingStrategy.getNetLabelNameForNamingGroup(label, instance, group));
                        this.foldName(netLabelByFold, name);
                        candidates.push({ name, isHarnessSynthetic: this.isHarnessSynthetic(label), labelType: 'net-label' });
                        continue;
                    }
                    const isSyntheticHarnessEntry = label.labelType === 'sheet-entry' &&
                        label.strategyMetadata?.harness?.harnessSource?.startsWith('synthetic-');
                    if (canUseSheetEntries &&
                        label.labelType === 'sheet-entry' &&
                        !isSyntheticHarnessEntry &&
                        this.canUseSheetEntryName(group, label)) {
                        const name = this.dereferenceLabelName(label.formattedLabel ?? label.originalLabel);
                        this.foldName(sheetEntryByFold, name);
                        candidates.push({ name, isHarnessSynthetic: this.isHarnessSynthetic(label), labelType: 'sheet-entry' });
                    }
                }
            }
        }
        for (const [_instance, nets] of group.getNets()) {
            for (const net of nets) {
                for (const label of net.labels) {
                    if (label.labelType !== 'net-label' || !label.formattedLabel) {
                        continue;
                    }
                    const formatted = this.dereferenceLabelName(label.formattedLabel);
                    const original = this.dereferenceLabelName(label.originalLabel);
                    if (formatted === original) {
                        continue;
                    }
                    if (netLabelByFold.has(original.toLowerCase())) {
                        netLabelByFold.delete(formatted.toLowerCase());
                        for (let i = candidates.length - 1; i >= 0; i--) {
                            if (candidates[i].name === formatted) {
                                candidates.splice(i, 1);
                            }
                        }
                    }
                }
            }
        }
        const netLabelNames = new Set(netLabelByFold.values());
        const sheetEntryNames = new Set(Array.from(sheetEntryByFold, ([fold, name]) => netLabelByFold.get(fold) ?? name));
        if (candidates.length === 0) {
            return undefined;
        }
        if (netLabelNames.size === 0 && group.hasLabelType('power-port')) {
            return undefined;
        }
        if (sheetEntryNames.size > 1) {
            const sharedNetLabelNames = Array.from(netLabelNames)
                .filter((name) => sheetEntryNames.has(name))
                .sort(compareLabelNamesOrdinal);
            if (sharedNetLabelNames.length > 0) {
                return sharedNetLabelNames[0];
            }
        }
        const differentialNetLabelNames = Array.from(netLabelNames)
            .filter((name) => this.hasSheetEntryWithoutSeparatedPolaritySuffix(name, sheetEntryNames))
            .sort(compareLabelNamesOrdinal);
        if (differentialNetLabelNames.length > 0) {
            return differentialNetLabelNames[0];
        }
        const normalizedCandidates = candidates.map((candidate) => ({
            ...candidate,
            name: candidate.labelType === 'sheet-entry' ?
                (netLabelByFold.get(candidate.name.toLowerCase()) ??
                    sheetEntryByFold.get(candidate.name.toLowerCase()) ??
                    candidate.name)
                : (netLabelByFold.get(candidate.name.toLowerCase()) ?? candidate.name),
        }));
        const demotedHarnessSyntheticCandidates = this.getDemotedHarnessSyntheticCandidates(normalizedCandidates);
        const sorted = normalizedCandidates.sort((a, b) => {
            const aBusMember = busMemberFolds.has(a.name.toLowerCase()) ? 0 : 1;
            const bBusMember = busMemberFolds.has(b.name.toLowerCase()) ? 0 : 1;
            if (aBusMember !== bBusMember) {
                return aBusMember - bBusMember;
            }
            const aEntry = a.labelType === 'sheet-entry' ? 1 : 0;
            const bEntry = b.labelType === 'sheet-entry' ? 1 : 0;
            if (aEntry !== bEntry) {
                return aEntry - bEntry;
            }
            const aDemoted = demotedHarnessSyntheticCandidates.has(a) ? 1 : 0;
            const bDemoted = demotedHarnessSyntheticCandidates.has(b) ? 1 : 0;
            if (aDemoted !== bDemoted) {
                return aDemoted - bDemoted;
            }
            return compareLabelNamesOrdinal(a.name, b.name);
        });
        return sorted[0].name;
    }
    getDemotedHarnessSyntheticCandidates(candidates) {
        const demotedCandidates = new Set();
        for (const candidate of candidates) {
            const segments = candidate.name.split('.');
            if (!candidate.isHarnessSynthetic || segments.length < 2) {
                continue;
            }
            const root = segments[0].toLowerCase();
            const hasHigherPrioritySameRootCandidate = candidates.some((otherCandidate) => {
                const otherSegments = otherCandidate.name.split('.');
                return (otherCandidate.labelType === 'net-label' &&
                    otherCandidate.name !== candidate.name &&
                    otherSegments.length >= 2 &&
                    otherSegments[0].toLowerCase() === root &&
                    (!otherCandidate.isHarnessSynthetic || otherSegments.length < segments.length));
            });
            if (hasHigherPrioritySameRootCandidate) {
                demotedCandidates.add(candidate);
            }
        }
        return demotedCandidates;
    }
    foldName(byFold, name) {
        const fold = name.toLowerCase();
        const existing = byFold.get(fold);
        byFold.set(fold, !existing || (0, AltiumNetNameCaseSelection_1.compareAltiumNetNameCaseVariants)(name, existing) < 0 ? name : existing);
    }
    isHarnessSynthetic(label) {
        return label.strategyMetadata?.harness?.harnessSource?.startsWith('synthetic-') ?? false;
    }
    hasSheetEntryWithoutSeparatedPolaritySuffix(name, sheetEntryNames) {
        if (!/_([PN])$/i.test(name)) {
            return false;
        }
        return sheetEntryNames.has(name.replace(/_([PN])$/i, '$1'));
    }
    canUseSheetEntryName(group, label) {
        const metadata = label.strategyMetadata?.['sheet-entry-metadata'];
        if (!metadata) {
            return (0, AltiumSheetEntryNamingEligibility_1.isAltiumSheetEntryNamingEligible)(group, label, this.config.mode, false);
        }
        return ((0, AltiumSheetEntryNamingEligibility_1.isAltiumSheetEntryNamingEligible)(group, label, this.config.mode, false) &&
            !metadata.isRepeated &&
            metadata.channelIndexes.length <= 1 &&
            metadata.ioType !== AltiumEnums_1.PortIOType.Input);
    }
}
exports.AltiumNetLabelNamingRule = AltiumNetLabelNamingRule;
//# sourceMappingURL=AltiumNetLabelNamingRule.js.map